import { Theme, useTheme } from '@mui/material/styles';

import Box from '@mui/material/Box';

import FormControl from '@mui/material/FormControl';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import Popper from '@mui/material/Popper';
import TextField from '@mui/material/TextField';

import { Field, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { IItemWithString } from 'lib/type/common';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import {
  buildEmailDomainSuggestions,
  filterEmailDomainSuggestions,
  getEmailDomainNames,
} from 'src/components/shared/EmailAutocomplete/emailDomainSuggestions.util';
import { ControllerRenderProps, FieldValues } from 'react-hook-form';
import { StFormLabel } from './ConnectUsForm.styles';
import ErrorIcon from './error_icon.svg';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { DEFAULT_PRIMARY_FONT_FAMILY, DEFAULT_PRIMARY_SEMIBOLD_FONT } from 'theme/fontDefaults';
import { getContactEmailDomainsListForContactInfo } from 'src/GraphQL/queries/contactEmailDomainsList.query';

interface IEmailAutocompleteProps {
  emailTitle: Field<string>;
  emailDomains: IItemWithString[];
  fieldProps: ControllerRenderProps<FieldValues, 'emailAddress'>;
  name: string;
  errors: any;
  isMobile: boolean;
  formElements: any;
}

const ConnectUsemail: React.FC<IEmailAutocompleteProps> = (props) => {
  const theme = useTheme();
  const [domainNames, setDomainNames] = useState<string[]>([]);
  const [domainList, setDomainList] = useState<string[]>([]);
  const emailValue = String(props.fieldProps?.value ?? '');
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;
  const language = sitecoreContext?.language || process.env.DEFAULT_LANGUAGE || 'en';

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await getContactEmailDomainsListForContactInfo(language, siteSettings);
        if (!cancelled) {
          setDomainNames(getEmailDomainNames(list));
        }
      } catch (error) {
        console.error('Error fetching email domains:', error);
        if (!cancelled) {
          setDomainNames([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [language, siteSettings]);

  useEffect(() => {
    const newValue = props?.fieldProps?.value ? props?.fieldProps?.value : '';
    setDomainList(buildEmailDomainSuggestions(newValue, domainNames));
  }, [props?.fieldProps?.value, domainNames]);

  const handleInputChange = (_event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    props?.fieldProps?.onChange(value);
  };

  const handleOnChange = (_event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    props?.fieldProps.onChange(value);
  };

  const filterOptions = (options: string[], { inputValue }: { inputValue: string }) => {
    const effectiveInput = (inputValue ?? '').trim() || emailValue;
    return filterEmailDomainSuggestions(options, effectiveInput);
  };

  return (
    <FormControl sx={{ width: props?.isMobile ? '300px' : '450px', gap: '0.25rem' }}>
      <StFormLabel htmlFor={props?.name}>
        <Text field={props?.emailTitle} />
      </StFormLabel>
      <Autocomplete
        freeSolo
        {...props.fieldProps}
        inputValue={emailValue}
        openOnFocus={domainList.length > 0}
        sx={{
          background: theme.palette.white.main,
          borderRadius: '0.5rem',
          '.MuiOutlinedInput-root .MuiAutocomplete-input': {
            color: theme.palette.black.main,
            padding: '0',
          },
          '.MuiAutocomplete-popper': { background: 'red' },
          '.MuiInputBase-root': {
            padding: '10px 15px',
          },
          '.MuiInputBase-input': {
            padding: '4px 0',
            fontFamily: `${
              siteSettings?.fontFamily?.fields?.primaryFontFamilyType?.value ||
              DEFAULT_PRIMARY_FONT_FAMILY
            }, sans-serif`,
            fontSize: '16px',
            lineHeight: '16px',
          },
        }}
        disableClearable
        options={domainList}
        filterOptions={filterOptions}
        onInputChange={handleInputChange}
        onChange={handleOnChange}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={props?.formElements?.fields?.placeholder?.value || ''}
            sx={{
              '& fieldset': { border: 'none' },
              border: (theme: Theme) =>
                props?.errors['emailAddress'] !== undefined
                  ? `1px solid ${theme.palette.error.main} !important`
                  : `1px solid ${theme.palette.neutral3.main}`,
              borderRadius: '0.5rem',
            }}
            inputProps={{
              ...params.inputProps,
              maxLength: props?.formElements?.fields?.maxLength?.value,
              'aria-label': 'Email Address',
              'aria-required': true,
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {props?.errors['emailAddress'] !== undefined ? (
                    <InputAdornment
                      position="start"
                      sx={{
                        minHeight: '15px',
                        minWidth: '15px',
                      }}
                    >
                      <Image src={ErrorIcon} width={15} height={15} alt="Error icon" />
                    </InputAdornment>
                  ) : (
                    <></>
                  )}
                </>
              ),
            }}
          />
        )}
        renderOption={(params, option) => (
          <span {...params} style={{ color: theme.palette.black.main }}>
            {option}
          </span>
        )}
        PopperComponent={({ ...props }) => (
          <Popper
            {...props}
            modifiers={[
              {
                name: 'offset',
                options: {
                  offset: [0, 10],
                },
              },
            ]}
          />
        )}
        noOptionsText=""
      />
      <>
        {props?.errors['emailAddress'] !== undefined && (
          <Box
            sx={{
              fontSize: '12px',
              fontFamily: `${
                siteSettings?.fontFamily?.fields?.primaryFontSemiBoldType ||
                DEFAULT_PRIMARY_SEMIBOLD_FONT
              }`,
            }}
          >
            {props?.errors['emailAddress'].message ||
              props?.formElements?.fields?.regexErrorMessage?.value}
          </Box>
        )}
      </>
    </FormControl>
  );
};

export default ConnectUsemail;
